import { Fragment, useMemo, useState } from 'react'
import { ArrowDown, ArrowUp, ChevronDown, ChevronRight, GripVertical, Loader2, SlidersHorizontal } from 'lucide-react'
import { Button, Checkbox, Popover, PopoverContent, PopoverTrigger } from './ui'
import { channelColor } from '../lib/channels'
import { CAMPAIGN_ROW_KEYS, CHANNEL_COLUMNS, DEFAULT_CHANNEL_COLUMNS, moveColumn, channelDelta, channelReportRows, formatChannelMetric, sortChannelRows } from '../lib/channelReport'
import { missingDaysBrief, missingDaysNote } from '../lib/spendCoverage'
import { channelAttribution } from '../lib/channelAttribution'

/**
 * Show, hide and reorder a report's columns. Defaults to the Channels catalogue;
 * another report passes its own `catalog` (already narrowed to what it can
 * show) and `defaults`.
 */
export function ChannelColumnPicker({ preferences, onChange, catalog = CHANNEL_COLUMNS, defaults = DEFAULT_CHANNEL_COLUMNS, pinned = 'Channel' }) {
  const available = catalog
  const visibleCount = available.filter(column => preferences.visible.includes(column.key)).length
  const ordered = preferences.order.map(key => available.find(column => column.key === key)).filter(Boolean)
  const reorder = (key, target) => {
    if (!target || target === key) return
    onChange({ ...preferences, order: moveColumn(preferences.order, key, target) })
  }
  // Arrows stay beside the drag handle: native drag does nothing on touch
  // screens or from the keyboard.
  const move = (key, offset) => reorder(key, ordered[ordered.findIndex(column => column.key === key) + offset]?.key)
  const [dragging, setDragging] = useState(null)
  const [over, setOver] = useState(null)
  const endDrag = () => { setDragging(null); setOver(null) }
  return <Popover>
    <PopoverTrigger asChild><Button variant="outline" size="sm"><SlidersHorizontal className="mr-2 h-4 w-4" />Columns <span className="ml-2 text-muted-foreground">{visibleCount}</span></Button></PopoverTrigger>
    <PopoverContent align="end" className="w-80">
      <div className="mb-3 flex items-center justify-between"><span className="text-sm font-semibold">Report columns</span><button className="text-xs text-primary hover:underline" onClick={() => onChange({ ...preferences, order: [...new Set([...catalog.map(column => column.key), ...preferences.order])], visible: defaults })}>Reset</button></div>
      <p className="mb-3 text-xs text-muted-foreground">Show, hide, and drag to reorder metrics. {pinned} stays pinned.</p>
      <div className="max-h-[60vh] space-y-1 overflow-y-auto pr-1">
        {ordered.map((column, index) => {
          const checked = preferences.visible.includes(column.key)
          const label = column.label
          return <div key={column.key} draggable
            onDragStart={event => { setDragging(column.key); event.dataTransfer.effectAllowed = 'move'; event.dataTransfer.setData('text/plain', column.key) }}
            onDragOver={event => { if (!dragging) return; event.preventDefault(); event.dataTransfer.dropEffect = 'move'; setOver(column.key) }}
            onDrop={event => { event.preventDefault(); reorder(dragging, column.key); endDrag() }}
            onDragEnd={endDrag}
            className={`flex items-center gap-2 rounded px-1 py-1 hover:bg-muted/50 ${dragging === column.key ? 'opacity-40' : ''} ${over === column.key && dragging && dragging !== column.key ? 'ring-1 ring-primary' : ''}`}>
            <GripVertical className="h-3.5 w-3.5 shrink-0 cursor-grab text-muted-foreground active:cursor-grabbing" aria-hidden="true" />
            <label className="flex flex-1 items-center gap-2 text-sm cursor-pointer">
              <Checkbox checked={checked} disabled={checked && visibleCount === 1} onCheckedChange={() => onChange({ ...preferences, visible: checked ? preferences.visible.filter(key => key !== column.key) : [...preferences.visible, column.key] })} />{label}
            </label>
            <button className="rounded p-1 hover:bg-muted disabled:opacity-30" disabled={index === 0} aria-label={`Move ${label} left`} onClick={() => move(column.key, -1)}><ArrowUp className="h-3.5 w-3.5" /></button>
            <button className="rounded p-1 hover:bg-muted disabled:opacity-30" disabled={index === ordered.length - 1} aria-label={`Move ${label} right`} onClick={() => move(column.key, 1)}><ArrowDown className="h-3.5 w-3.5" /></button>
          </div>
        })}
      </div>
    </PopoverContent>
  </Popover>
}

function MetricCell({ row, column, previous, compare }) {
  const delta = compare ? channelDelta(row?.[column.key], previous?.[column.key]) : null
  const value = formatChannelMetric(row?.[column.key], column.type)
  const showCoverage = (item) => column.key === 'spend' && item?.missing_spend_days?.length
    ? <span className="block text-[11px] text-amber-700 dark:text-amber-400" title={missingDaysNote(item.missing_spend_days, { limit: 366 })}>{missingDaysBrief(item.missing_spend_days, { limit: 2 })}</span>
    : null
  const directional = !column.neutral && !['spend', 'refunds'].includes(column.key)
  const good = delta && (column.lowerIsBetter ? delta.absolute < 0 : delta.absolute > 0)
  return <td className="px-4 py-3 text-right align-top whitespace-nowrap">
    <span className="font-medium">{value}</span>
    {showCoverage(row)}
    {compare && <>
      <span className="mt-1 block text-xs text-muted-foreground" aria-label="Comparison value">{formatChannelMetric(previous?.[column.key], column.type)}</span>
      {showCoverage(previous)}
      <span className={`mt-1 block text-xs ${delta?.absolute && directional ? good ? 'text-emerald-700 dark:text-emerald-400' : 'text-red-600 dark:text-red-400' : 'text-muted-foreground'}`}
        title={delta ? `Change: ${delta.absolute > 0 ? '+' : ''}${formatChannelMetric(delta.absolute, column.type)}${delta.percent == null ? '; no percentage baseline' : ''}` : 'Comparison unavailable'}>
        {delta == null ? '—' : delta.percent == null ? (delta.absolute === 0 ? 'No change' : `${delta.absolute > 0 ? '+' : ''}${formatChannelMetric(delta.absolute, column.type)} · new baseline`) : `${delta.percent > 0 ? '+' : ''}${delta.percent.toFixed(1)}%`}
      </span>
    </>}
  </td>
}

/** A campaign or content row's metric: blank where the column has no campaign-level figure. */
function CampaignCell({ row, column, depth }) {
  if (!CAMPAIGN_ROW_KEYS.has(column.key)) return <td />
  const value = row[column.key]
  return <td className={`px-4 py-2 text-right align-top whitespace-nowrap ${depth ? 'text-xs' : ''} ${value == null ? 'text-muted-foreground' : ''}`}>{formatChannelMetric(value, column.type)}</td>
}

function campaignName(row) {
  if (row.campaignOnly) return <em className="text-muted-foreground" title="Credit whose click named the campaign but no ad or content.">Credited to the campaign only</em>
  if (row.noCampaign) return <em className="text-muted-foreground" title="Credit whose touch carried no utm_campaign and matched no ad account campaign.">No campaign recorded</em>
  return row.label
}

/**
 * A channel's campaign → content rows under the selected model: the same
 * columns as the channel row, so a channel's campaigns read (and add up)
 * against it. Current period only.
 */
function ChannelCampaignRows({ channel, campaigns, columns }) {
  const [open, setOpen] = useState(() => new Set())
  const span = columns.length + 1
  if (campaigns.loading) return <tr className="border-t bg-muted/10"><td colSpan={span} className="px-4 py-2"><span role="status" className="sticky left-4 flex items-center gap-2 text-xs text-muted-foreground"><Loader2 className="h-3 w-3 animate-spin" />Attributing sales to campaigns…</span></td></tr>
  if (campaigns.error) return <tr className="border-t bg-muted/10"><td colSpan={span} className="px-4 py-2"><span role="alert" className="sticky left-4 text-xs text-destructive">Campaigns unavailable: {campaigns.error}</span></td></tr>
  const rows = campaigns.byChannel?.get(channel) || []
  if (!rows.length) return <tr className="border-t bg-muted/10"><td colSpan={span} className="px-4 py-2"><span className="sticky left-4 text-xs text-muted-foreground">No credited campaigns for this channel in these dates.</span></td></tr>
  const nameCell = (row, depth, toggle) => {
    const Chevron = toggle?.isOpen ? ChevronDown : ChevronRight
    return <th scope="row" className={`sticky left-0 z-10 bg-background py-2 pr-4 text-left align-top text-sm font-normal group-hover:bg-muted ${depth ? 'pl-16 text-xs' : 'pl-9'}`}>
      <span className="flex max-w-xs items-start gap-1.5 break-words">
        {toggle
          ? <button type="button" onClick={toggle.onClick} aria-expanded={toggle.isOpen} aria-label={toggle.isOpen ? 'Hide content' : 'Show content'} className="mt-0.5 rounded hover:bg-muted"><Chevron className="h-3.5 w-3.5 text-muted-foreground" /></button>
          : !depth && <span className="w-3.5 shrink-0" />}
        <span>{campaignName(row)}</span>
      </span>
    </th>
  }
  return rows.flatMap(campaign => {
    const isOpen = open.has(campaign.key)
    const toggle = campaign.contents.length ? { isOpen, onClick: () => setOpen(previous => {
      const next = new Set(previous)
      next.has(campaign.key) ? next.delete(campaign.key) : next.add(campaign.key)
      return next
    }) } : null
    return [
      <tr key={campaign.key} className="group border-t bg-muted/10 hover:bg-muted/20">
        {nameCell(campaign, 0, toggle)}
        {columns.map(column => <CampaignCell key={column.key} row={campaign} column={column} depth={0} />)}
      </tr>,
      ...(isOpen ? campaign.contents.map(content => <tr key={`${campaign.key}::${content.key}`} className="group border-t bg-muted/5 hover:bg-muted/15">
        {nameCell(content, 1)}
        {columns.map(column => <CampaignCell key={column.key} row={content} column={column} depth={1} />)}
      </tr>) : []),
    ]
  })
}

/** A channel's name, with how it is attributed on hover or keyboard focus. */
function ChannelName({ channel, label }) {
  const how = channelAttribution(channel)
  if (!how) return label
  const id = `attribution-${channel}`
  return <span className="group/how relative inline-flex">
    <span tabIndex={0} aria-describedby={id} className="cursor-help underline decoration-dotted decoration-muted-foreground/50 underline-offset-4 focus:outline-none">{label}</span>
    <span id={id} role="tooltip" className="pointer-events-none invisible absolute left-full top-1/2 z-30 ml-3 w-72 -translate-y-1/2 rounded-md border bg-popover px-3 py-2 text-left text-xs font-normal leading-snug whitespace-normal text-popover-foreground opacity-0 shadow-md transition-opacity group-hover/how:visible group-hover/how:opacity-100 group-focus-within/how:visible group-focus-within/how:opacity-100">{how}</span>
  </span>
}

export default function ChannelReportTable({ current, comparison, columns, search = '', comparisonLabel, campaigns = null, onOpenChannel }) {
  const [sort, setSort] = useState({ key: 'revenue', direction: 'desc' })
  const [open, setOpen] = useState(() => new Set())
  // Until the breakdown loads every channel may have campaigns; after, only those that do.
  const expandable = channel => Boolean(campaigns) && !['unattributed', 'outside_window'].includes(channel)
    && (!campaigns.byChannel || campaigns.byChannel.has(channel))
  const toggleOpen = channel => {
    onOpenChannel?.()
    setOpen(previous => {
      const next = new Set(previous)
      next.has(channel) ? next.delete(channel) : next.add(channel)
      return next
    })
  }
  const activeSort = sort.key === 'channel' || columns.some(column => column.key === sort.key) ? sort : { key: columns[0]?.key || 'channel', direction: 'desc' }
  const rows = useMemo(() => channelReportRows(current, comparison), [current, comparison])
  const filtered = sortChannelRows(rows.filter(row => row.label.toLowerCase().includes(search.trim().toLowerCase())), activeSort)
  const toggleSort = key => setSort({ key, direction: activeSort.key === key && activeSort.direction === 'desc' ? 'asc' : 'desc' })
  // A column's definition shows on hover or keyboard focus, anchored to the
  // header's right edge so the last columns' definitions stay inside the table.
  const header = (key, label, align, definition = null) => <th key={key} scope="col" aria-sort={activeSort.key === key ? activeSort.direction === 'asc' ? 'ascending' : 'descending' : 'none'} className={`bg-muted px-4 py-3 text-${align} text-xs font-medium whitespace-nowrap ${key === 'channel' ? 'sticky left-0 z-20 min-w-44' : 'min-w-36'}`}>
    <span className="group/definition relative inline-flex">
      <button className={`inline-flex items-center gap-1 hover:text-primary ${definition ? 'cursor-help underline decoration-dotted decoration-muted-foreground/50 underline-offset-4' : ''}`} onClick={() => toggleSort(key)} aria-describedby={definition ? `definition-${key}` : undefined}>{label}{activeSort.key === key && (activeSort.direction === 'desc' ? <ArrowDown className="h-3 w-3" /> : <ArrowUp className="h-3 w-3" />)}</button>
      {definition && <span id={`definition-${key}`} role="tooltip" className={`pointer-events-none invisible absolute top-full z-30 mt-2 w-64 rounded-md border bg-popover px-3 py-2 text-left text-xs font-normal leading-snug whitespace-normal text-popover-foreground opacity-0 shadow-md transition-opacity group-hover/definition:visible group-hover/definition:opacity-100 group-focus-within/definition:visible group-focus-within/definition:opacity-100 ${align === 'right' ? 'right-0' : 'left-0'}`}>{definition}</span>}
    </span>
  </th>
  return <div className="overflow-auto max-h-[70vh] rounded-md border">
    <table className="w-full text-sm tabular-nums">
      <caption className="sr-only">Channel report using {current.label}{comparison ? ` compared with ${comparisonLabel}` : ''}. {comparison ? 'Each metric shows current, comparison, and percentage change. Sorting uses current values.' : ''}</caption>
      <thead className="sticky top-0 z-20"><tr>{header('channel', 'Channel', 'left')}{columns.map(column => header(column.key, column.displayLabel, 'right', column.definition))}</tr></thead>
      <tbody>
        {filtered.map(row => {
          const canOpen = expandable(row.channel)
          const isOpen = canOpen && open.has(row.channel)
          const Chevron = isOpen ? ChevronDown : ChevronRight
          return <Fragment key={row.channel}>
            <tr className="group border-t hover:bg-muted/30">
              <th scope="row" className="sticky left-0 z-10 bg-background px-4 py-3 text-left align-top font-medium whitespace-nowrap group-hover:z-30 group-hover:bg-muted">
                <span className="inline-flex items-center gap-2">
                  {canOpen
                    ? <button type="button" onClick={() => toggleOpen(row.channel)} aria-expanded={isOpen} aria-label={`${isOpen ? 'Hide' : 'Show'} ${row.label} campaigns`} className="-ml-1 rounded hover:bg-muted"><Chevron className="h-4 w-4 text-muted-foreground" /></button>
                    : campaigns && <span className="w-3" />}
                  <span className="h-2 w-2 rounded-sm" style={{ backgroundColor: channelColor(row.channel) }} />
                  <ChannelName channel={row.channel} label={row.label} />
                </span>
              </th>
              {columns.map(column => <MetricCell key={column.key} row={row.current} previous={row.comparison} column={column} compare={Boolean(comparison)} />)}
            </tr>
            {isOpen && <ChannelCampaignRows channel={row.channel} campaigns={campaigns} columns={columns} />}
          </Fragment>
        })}
        {!filtered.length && <tr><td colSpan={columns.length + 1} className="p-10 text-center text-muted-foreground">{search ? 'No channels match your search.' : 'No purchases or spend in these dates. Try a wider date range.'}</td></tr>}
      </tbody>
      <tfoot className="border-t-2 bg-muted/40">
        <tr><th scope="row" className="sticky left-0 z-10 bg-muted px-4 py-3 text-left align-top font-semibold whitespace-nowrap">{current.overlapping ? 'Total overlapping credit' : current.model ? 'Total credit' : 'Total'}{search && <span className="block text-xs font-normal text-muted-foreground">All channels</span>}</th>
          {columns.map(column => <MetricCell key={column.key} row={current.totals} previous={comparison?.totals} column={column} compare={Boolean(comparison)} />)}
        </tr>
      </tfoot>
    </table>
  </div>
}
